import { setResponseHeader } from 'h3'
import { AUTHORISED_CONTENT_ENABLED } from '~~/layers/core/app/util/features'

export const useEntryStore = defineStore('entryStore', {
    state: () => ({
        entry: {},
        seomatic: {},
        variables: {},
        seoHeadScripts: [],
        seoBodyScripts: [],
        seoBodyNoscripts: [],
        currentUri: '', // Track current URI for reload functionality
        currentArticle: { type: '', slug: '' }, // Track current article for reload
    }),
    actions: {
        async loadEntry(uri, options?: { routeQuery?: Record<string, any> }) {
            this.currentUri = uri
            this.currentArticle = { type: '', slug: '' }

            const { cmsQuery } = useGraphQL()

            const { data: pageData } = await cmsQuery({
                uri: `page-data/page/${uri}`,
                key: `entry-${uri}`,
                routeQuery: options?.routeQuery,
            })

            // Check if there are errors in the response (could indicate protected content)
            const hasErrors = pageData.value?.errors?.length > 0
            if (hasErrors) {
                console.warn('CMS returned errors:', pageData.value?.errors)
            }

            const redirect = get(pageData.value?.data, 'retourResolveRedirect', {})

            if (redirect && !isEmpty(redirect.redirectDestUrl) && redirect.enabled) {
                const routeQuery = options?.routeQuery ?? useRoute().query
                return navigateTo(
                    {
                        path: redirect.redirectDestUrl,
                        query: routeQuery,
                    },
                    {
                        redirectCode: redirect.redirectHttpCode || 301,
                        replace: true,
                        external: true,
                    },
                )
            }

            // replace page data entry
            this.entry = get(pageData.value?.data, 'entry', {})

            // Check if content is protected - don't throw 404 for protected content
            if (AUTHORISED_CONTENT_ENABLED) {
                const authorisedContent = get(this.entry, 'authorisedContent', {})
                if (authorisedContent.isProtected && !authorisedContent.isAuthorized) {
                    // Prevent the frontend CloudFront distribution from caching the
                    // gate HTML. The authorisation decision must always be evaluated
                    // fresh; a cached gate page would show the gate to authenticated
                    // users until the CloudFront TTL expires.
                    if (import.meta.server) {
                        const event = useNuxtApp().ssrContext?.event
                        if (event) {
                            setResponseHeader(event, 'Cache-Control', 'no-store, no-cache, must-revalidate, max-age=0')
                        }
                    }
                    // Content exists but is protected - allow rendering with gate UI
                    return
                }
            }
            if (isEmpty(this.entry?.slug)) {
                throw createError({
                    statusMessage: hasErrors ? 'Page failed to load' : 'Page not found',
                    message: hasErrors
                        ? 'The page could not be loaded. Please try again or contact support if the problem persists.'
                        : 'The page you are looking for does not exist. <br /><br />Try finding what you are looking for by searching from the top menu or return back to the homepage',
                    statusCode: hasErrors ? 500 : 404,
                })
            }

            // Handle RedirectPage entries
            if (this.entry?.__typename === 'ds_RedirectPage_Entry') {
                const redirectUrl = this.entry?.redirectLink?.[0].linkUrl

                if (redirectUrl) {
                    const routeQuery = options?.routeQuery ?? useRoute().query
                    return navigateTo(
                        {
                            path: redirectUrl,
                            query: routeQuery,
                        },
                        {
                            redirectCode: 301,
                            replace: true,
                            external: true,
                        },
                    )
                }
            }

            this.initEntry()
        },

        async loadNewsletterArticle(newsletter: string, slug: string) {
            this.currentArticle = { newsletter, slug }
            this.currentUri = ''

            const { cmsQuery } = useGraphQL()

            const {data: articleData } = await cmsQuery({
                uri: `page-data/newsletter-article/${newsletter}/${slug}`,
                key: `entry-${newsletter}-${slug}`,
            })

            // Check if there are errors in the response (could indicate protected content)
            const hasErrors = articleData.value?.errors?.length > 0
            if (hasErrors) {
                console.warn('CMS returned errors:', articleData.value?.errors)
            }

            // replace page data entry
            this.entry = get(articleData.value?.data, 'entry', {})

            // Check if content is protected - don't throw 404 for protected content
            if (AUTHORISED_CONTENT_ENABLED) {
                const authorisedContent = get(this.entry, 'authorisedContent', {})
                if (authorisedContent.isProtected && !authorisedContent.isAuthorized) {
                    // Same as loadEntry: prevent the frontend CloudFront from caching
                    // the gate HTML for SSR-rendered article pages.
                    if (import.meta.server) {
                        const event = useNuxtApp().ssrContext?.event
                        if (event) {
                            setResponseHeader(event, 'Cache-Control', 'no-store, no-cache, must-revalidate, max-age=0')
                        }
                    }
                    // Content exists but is protected - allow rendering with gate UI
                    return
                }
            }

            if (isEmpty(this.entry?.slug)) {
                throw createError({
                    statusMessage: hasErrors ? 'Article failed to load' : 'Page not found',
                    message: hasErrors
                        ? 'The article could not be loaded. Please try again or contact support if the problem persists.'
                        : 'The page you are looking for does not exist. <br /><br />Try finding what you are looking for by searching from the top menu or return back to the homepage',
                    statusCode: hasErrors ? 500 : 404,
                })
            }

            this.initEntry()
        },

        initEntry() {
            this.seomatic = get(this.entry, 'seomatic', {})
            this.variables = JSON.parse(get(this.seomatic, 'metaSiteVarsContainer', '{}'))

            const scripts = Object.values(JSON.parse(get(this.seomatic, 'metaScriptContainer', '{}')))

            // Separate head and body scripts
            this.seoHeadScripts = scripts
                .filter((script) => script.script)
                .map((script) => ({
                    innerHTML: script.script,
                    type: 'text/javascript',
                }))

            // Parse body scripts from HTML strings, extracting script tags and their attributes
            this.seoBodyScripts = scripts
                .filter((script) => script.bodyScript)
                .flatMap((script) =>
                    Array.from(
                        script.bodyScript.matchAll(/<script\b([^>]*)>([\s\S]*?)<\/script(?:\s[^>]*)?\s*>/gi),
                        (match) => {
                            const [, attributes, content] = match
                            const srcMatch = attributes.match(/src=["']([^"']+)["']/)
                            const typeMatch = attributes.match(/type=["']([^"']+)["']/)

                            return {
                                tagPosition: 'bodyClose',
                                ...(srcMatch && { src: srcMatch[1] }),
                                ...(attributes.includes('async') && { async: true }),
                                ...(attributes.includes('defer') && { defer: true }),
                                ...(typeMatch && { type: typeMatch[1] }),
                                ...(content.trim() && { innerHTML: content.trim() }),
                            }
                        },
                    ),
                )

            // Extract noscript tags from body scripts
            this.seoBodyNoscripts = scripts
                .filter((script) => script.bodyScript)
                .flatMap((script) =>
                    Array.from(script.bodyScript.matchAll(/<noscript>(.*?)<\/noscript>/gis), (match) => ({
                        innerHTML: match[1].trim(),
                        tagPosition: 'bodyClose',
                    })),
                )
        },

        /**
         * Reload the current entry with fresh data.
         * Used after authorization to fetch protected content with the new token.
         * Forces a client-side fetch to bypass cache and include new gate tokens.
         */
        async reloadEntry() {
            const { cmsQuery } = useGraphQL()

            if (this.currentArticle.type && this.currentArticle.slug) {
                const { type, slug } = this.currentArticle

                // When server: false, cmsQuery returns { data: graphqlData } directly (not a ref)
                // graphqlData contains { entry: {...} }
                const result = await cmsQuery({
                    uri: `page-data/article/${type}/${slug}`,
                    key: `entry-${type}-${slug}-reload-${Date.now()}`,
                    server: false, // Force client-side fetch
                })

                console.log('[reloadEntry] article result:', result)
                this.entry = get(result?.data, 'entry', {})
                this.initEntry()
                return
            }

            if (this.currentUri) {
                // When server: false, cmsQuery returns { data: graphqlData } directly (not a ref)
                // graphqlData contains { entry: {...} }
                const result = await cmsQuery({
                    uri: `page-data/page/${this.currentUri}`,
                    key: `entry-${this.currentUri}-reload-${Date.now()}`,
                    server: false, // Force client-side fetch
                })

                console.log('[reloadEntry] page result:', result)
                this.entry = get(result?.data, 'entry', {})
                this.initEntry()
                return
            }

            console.warn('Cannot reload entry: no URI or article info stored')
        },
    },
})
